"use client"

import { useState } from "react"
import { z } from "zod" // Import Zod
import { Phone, Mail, Clock, Send, Factory, Headset } from "lucide-react"
import "./Contact.css"

// 1. Define the Zod schema for strict validation
const contactSchema = z.object({
    name: z
      .string()
      .trim()
      .min(1, "Name is required")
      .min(3, "Name must be at least 3 characters"),
    email: z
      .string()
      .email("Please enter a valid email address")
      .optional()
      .or(z.literal("")), // Allow empty string for optional field
    phone: z
      .string()
      .trim()
      .min(1, "Phone number is required")
      .regex(/^[0-9]{10,15}$/, "Please enter a valid phone number"),
    company: z.string().optional(),
    subject: z.string().min(1, "Please select an inquiry type"),
    message: z
      .string()
      .trim()
      .min(1, "Message is required")
      .min(10, "Message must be at least 10 characters"),
  })
  .strict() // Ensures no extra fields are submitted

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
  })

  // The errors state remains, but will be populated by Zod
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })

    // Clear the error for a field when the user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      })
    }
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    // Validate the field on blur using the Zod schema
    const fieldSchema = contactSchema.shape[name]
    const result = fieldSchema.safeParse(value)

    if (!result.success) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: result.error.flatten().formErrors[0],
      }))
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }))
    }
  }
  
  const sendEmail = async (data) => {
    // Using Formspree service
    try {
      const response = await fetch("https://formspree.io/f/mnnzvpwd", { 
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...data,
          _replyto: data.email || 'no-reply@imperia.com', // Fallback email
        }),
      })

      if (!response.ok) throw new Error("Email sending failed")
      return true
    } catch (error) {
      console.error("Email error:", error)
      return false
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitStatus(null)

    // 2. Validate form data with Zod
    const validationResult = contactSchema.safeParse(formData)

    if (!validationResult.success) {
      // If validation fails, update the errors state
      const fieldErrors = validationResult.error.flatten().fieldErrors
      setErrors({
        name: fieldErrors.name?.[0] || "",
        email: fieldErrors.email?.[0] || "",
        phone: fieldErrors.phone?.[0] || "",
        subject: fieldErrors.subject?.[0] || "",
        message: fieldErrors.message?.[0] || "",
      })
      setSubmitStatus("validation-error")
      return
    }

    // If validation succeeds, clear all errors and proceed
    setErrors({ name: "", email: "", phone: "", subject: "", message: "" })
    setIsSubmitting(true)
    
    // The validated data is in validationResult.data
    const validatedData = validationResult.data;

    try {
      // 1. Send WhatsApp message
      const whatsappMessage = `New Inquiry from Imperia Industries Website:

Name: ${validatedData.name}
Email: ${validatedData.email || 'Not provided'}
Phone: ${validatedData.phone}
Company: ${validatedData.company || 'Not provided'}
Inquiry Type: ${validatedData.subject}
Message: ${validatedData.message}

Please respond to this inquiry at your earliest convenience.`

      const encodedMessage = encodeURIComponent(whatsappMessage)
      const whatsappUrl = `https://wa.me/919350019570?text=${encodedMessage}`
      window.open(whatsappUrl, '_blank')

      // 2. Send email via Formspree using validated data
      const emailSent = await sendEmail(validatedData)
      
      if (emailSent) {
        setSubmitStatus("success")
        // Reset form on success
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          subject: "",
          message: "",
        })
      } else {
        setSubmitStatus("partial-success")
      }
    } catch (error) {
      console.error("Submission error:", error)
      setSubmitStatus("error")
    } finally {
      setIsSubmitting(false)
    }
  }

  const contactInfo = [
    {
      icon: <Factory size={24} />,
      title: "Address - Imperia Industries India Pvt. Ltd.",
      details: ["Plot No. 122, Block - C", "Sector 5, Bawana Industrial Park", "Delhi - 110039", "India"],
    },
    {
      icon: <Headset size={24} />,
      title: "Customer Support",
      details: ["Phone No.: +91 9350019570", "24/7 Technical Support"],
    },
    {
      icon: <Mail size={24} />,
      title: "Business Inquiries",
      details: ["Sales: imperiahoses@gmail.com"],
    },
    {
      icon: <Clock size={24} />,
      title: "Operations Hours",
      details: ["Monday-Sunday: 10:00 AM - 5:00 PM", "Saturday: Week Off"],
    },
  ]

  return (
    <div className="contact-page">
      <section className="contact-main section">
        <div className="container">
          <div className="section-intro">
            <h2 className="section-title">How Can We Help You?</h2>
            <p className="section-description">
              Complete the form below and one of our specialists will contact you within 24 hours. For
              immediate assistance, call our support line.
            </p>
          </div>

          <div className="contact-content">
            <div className="contact-form-section">
              <form onSubmit={handleSubmit} className="contact-form" noValidate>
                <div className="form-header">
                  <h3>Send Us Your Inquiry</h3>
                  <p>Fields marked with * are required</p>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      placeholder="Enter your full name"
                      className={errors.name ? "error" : ""}
                    />
                    {errors.name && <span className="error-message">{errors.name}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="your.email@example.com"
                      className={errors.email ? "error" : ""}
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      placeholder="+91 12345 67890"
                      className={errors.phone ? "error" : ""}
                    />
                    {errors.phone && <span className="error-message">{errors.phone}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="company">Company/Organization</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Your company name"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Inquiry Type *</label>
                  <select 
                    id="subject" 
                    name="subject" 
                    value={formData.subject} 
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    className={errors.subject ? "error" : ""}
                  >
                    <option value="">Select inquiry type</option>
                    <option value="product-info">Product Information</option>
                    <option value="technical-support">Technical Support</option>
                    <option value="quote-request">Quote Request</option>
                    <option value="custom-solution">Custom Solution</option>
                    <option value="distributor-inquiry">Distributor Inquiry</option>
                    <option value="service-request">Service Request</option>
                  </select>
                  {errors.subject && <span className="error-message">{errors.subject}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="message">Your Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    value={formData.message}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Please include details about your application, pressure requirements, flow rates, and any other specifications..."
                    required
                    className={errors.message ? "error" : ""}
                  ></textarea>
                  {errors.message && <span className="error-message">{errors.message}</span>}
                </div>

                <div className="form-footer">
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <div className="spinner"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Submit Inquiry
                      </>
                    )}
                  </button>
                  <p className="form-note">
                   Submit this form, Our team will soon contact you.
                  </p>
                </div>

                {submitStatus === "success" && (
                  <div className="form-message success-message">
                    <h4>Thank You!</h4>
                    <p>
                      Your inquiry has been received. Our technical team will review your requirements and contact you
                      shortly.
                    </p>
                  </div>
                )}

                {submitStatus === "partial-success" && (
                  <div className="form-message warning-message">
                    <h4>Message Sent!</h4>
                    <p>
                      Your WhatsApp message was sent successfully, but we encountered an issue with the email. 
                      Please ensure imperiahoses@gmail.com has your correct contact information.
                    </p>
                  </div>
                )}

                {submitStatus === "validation-error" && (
                  <div className="form-message error-message">
                    <h4>Form Errors</h4>
                    <p>
                      Please correct the highlighted errors and try again.
                    </p>
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="form-message error-message">
                    <h4>Submission Error</h4>
                    <p>
                      There was an issue sending your message. Please try again or contact us directly at
                      imperiahoses@gmail.com
                    </p>
                  </div>
                )}
              </form>
            </div>

            <div className="contact-info-section">
              <div className="info-header">
                <h3>Direct Contact Channels</h3>
                <p>Choose the most convenient way to reach our specialists</p>
              </div>

              <div className="contact-info-grid">
                {contactInfo.map((info, index) => (
                  <div key={index} className="contact-info-card">
                    <div className="info-icon">{info.icon}</div>
                    <div className="info-content">
                      <h4>{info.title}</h4>
                      <div className="info-details">
                        {info.details.map((detail, detailIndex) => (
                          <p key={detailIndex}>{detail}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="emergency-contact">
                <h4>Technical Support</h4>
                <p>For urgent queries or support</p>
                <a href="https://wa.me/919350019570" className="emergency-phone">
                  <Phone size={20} />
                  +91 9350019570
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="map-section">
        <div className="container">
          <div className="section-intro">
            <h2 className="section-title">Visit Our Headquarters</h2>
            <p className="section-description">
              Schedule a tour of our advanced hydraulic component manufacturing facility
            </p>
          </div>

          <div className="map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3507.2!2d77.0776596!3d28.7967007!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d1b2940b9d36b%3A0x1f1a98d0a773a6f2!2sImperia%20Industries%20India%20Pvt%20Ltd!5e0!3m2!1sen!2sin!4v1712345678901!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Imperia Industries India Location"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact